'use client'

import React, { memo, useCallback, useMemo } from 'react'
import { AddIcon } from '@/app/[locale]/_assets/icon-components'
import { Box, Card, Text } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { useProductModal } from '@/app/[locale]/_components/ProductDetailModal/context'
import CardHeader from './components/CardHeader'
import { IDiscount, IProduct } from '@/app/[locale]/_types'
import { IRedux } from '@/app/[locale]/_store/types'
import { useDispatch, useSelector } from 'react-redux'
import AddToCart from '@/app/[locale]/_components/AddToCart'
import useProductCardCart from '@/app/[locale]/_hooks/useProductCardCart'
import NumberToPrice from '@/app/[locale]/_components/NumberToPrice'
import { onOpenMap } from '@/app/[locale]/_store/common/common.slice'
import { useResolveLocaleField } from '@/app/[locale]/_hooks/useResolveLocaleField'
import {
  ADD_TO_CART,
  REMOVE,
  SET_QUANTITY,
} from '@/app/[locale]/_store/cart/cart.slice'
import useGTM from '@/app/[locale]/_hooks/useGTM'
import { track } from '@/app/[locale]/_utils/utm'
import { useProductQuickAdd } from '@/app/[locale]/_hooks/useProductQuickAdd'
import { getSelectedMenuId } from '@/app/[locale]/_utils/modifier/buildCompulsoryModifiers'
import type { ISavedModifier } from '@/app/[locale]/_types'

const ProductCard = ({
  priority,
  product,
  discounts,
  categoryName,
}: {
  priority?: boolean
  product: IProduct
  discounts?: IDiscount[]
  categoryName: string | undefined
}) => {
  const resolveLocale = useResolveLocaleField()
  const dispatch = useDispatch()
  const t = useTranslations()
  const { openProduct } = useProductModal()

  const { sendGTM } = useGTM()
  const { quantity, productInCart } = useProductCardCart(product)
  const { branch } = useSelector((state: IRedux) => state.common)
  const { data, country } = useSelector((state: IRedux) => state.settings)

  const { permissions, is_hall, menu_id, order_type } = useSelector(
    (state: IRedux) => state.session
  )

  const totalDiscount = useMemo(() => {
    let result = 0

    if (product.discounts?.length) {
      result = product.discounts?.reduce((acc, cur) => {
        return acc + cur.discount_price
      }, 0)
    } else if (discounts?.length) {
      result = discounts?.reduce((acc, cur) => {
        if (
          cur.discount_id === 'e46ce26c-5f1b-4ca6-89f1-c013ed5f58ce' &&
          product.id !== '673c4d0a-1368-40ff-bef5-32c57dbbf964'
        ) {
          return acc
        }

        if (
          cur?.product_and_discount &&
          Object.keys(cur?.product_and_discount).length > 0
        ) {
          if (cur.product_and_discount[product.id]) {
            acc += cur.product_and_discount[product.id]
          }
        } else if (cur.discount_for === 'product') {
          if (cur.discount_mode === 'fixed') {
            acc -= cur.discount_amount
          } else if (cur.discount_mode === 'percent') {
            acc -= (product.out_price / 100) * cur.discount_amount
          }
        }
        return acc
      }, 0)
    }

    return result
  }, [discounts])

  const navigateTo = useCallback(() => {
    if (
      is_hall === 'true' ||
      (order_type?.type === 'self-pickup' && branch?.id) ||
      (order_type?.type === 'delivery' &&
        permissions.geolocation === 'accepted')
    ) {
      sendGTM('select_item', { product, name: data?.data?.shipper_name })
      openProduct(product)
    } else {
      dispatch(onOpenMap())
    }
  }, [product, permissions.geolocation, order_type?.type, branch?.id])

  const addToCartHandler = useCallback(
    (orderModifiers: ISavedModifier[] = []) => {
    if (product.type === 'origin') return

    dispatch(
      ADD_TO_CART({
        key: '',
        comment: '',
        in_stop: false,
        default_product: false,
        category_name: categoryName,
        discount_percent:
          totalDiscount !== 0
            ? Math.abs(Math.round((totalDiscount / product?.out_price) * 100))
            : 0,
        product_name: resolveLocale(product.title),
        price_with_discount: product.out_price + totalDiscount,
        price: product.out_price,
        product_id: product.id,
        type: product.type,
        variants: {},
        quantity: 1,
        order_modifiers: orderModifiers,
        name: resolveLocale(product.title),
      })
    )
    track({
      action_type: 'add_to_cart',
      tg_chat_id: "",
    })
    sendGTM('add_to_cart', { product, quantity })
    sendGTM('add_to_cart_ads', { product, currency: country?.currency })
    sendGTM('add_to_cart_fb', { product, currency: country?.currency })

    if (navigator.vibrate) {
      navigator.vibrate(100)
    } else {
      console.warn('Vibration API is not supported on this device.')
    }
  },
    [product, dispatch, totalDiscount, resolveLocale, categoryName, sendGTM, quantity, country?.currency]
  )

  const selectedMenuId = getSelectedMenuId({
    isHall: is_hall === 'true',
    pathnameEndsWithMenu: false,
    menuId: menu_id,
    branchMenuId: branch?.menu_id,
  })

  const { handleAddClick } = useProductQuickAdd({
    product,
    menuId: selectedMenuId,
    isHall: is_hall === 'true',
    orderType: order_type?.type,
    branchId: branch?.id,
    geolocation: permissions.geolocation,
    onDirectAdd: addToCartHandler,
    onOpenDetail: navigateTo,
    onOpenMap: () => dispatch(onOpenMap()),
  })

  const removeHandler = () => {
    if (productInCart) {
      sendGTM('remove_from_cart', {
        cart: [productInCart],
        name: data?.data?.shipper_name,
      })
      dispatch(REMOVE(productInCart?.key))
    }
  }

  const isActiveProduct =
    is_hall === 'true'
      ? (menu_id !== 'null' && Boolean(product.active_in_menu)) ||
        menu_id == 'null'
      : (branch?.menu_id && product.active_in_menu) || !branch?.menu_id

  return (
    <Card.Root
      // ref={setRef}
      rounded={16}
      variant='subtle'
      bg={{
        base: 'background',
        lg: 'background.card',
      }}
      cursor={'pointer'}
      transition='box-shadow 0.2s ease-in-out'
      _hover={{
        boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
      }}
    >
      <Card.Header
        px={{ base: 2, lg: 4 }}
        pt={{ base: 2, lg: 4 }}
        onClick={navigateTo}
      >
        <CardHeader
          totalDiscount={totalDiscount}
          product={product}
          priority={priority}
        />
      </Card.Header>
      <Card.Body
        pb={3}
        position='relative'
        px={{ base: 2, lg: 4 }}
        pt={{ base: 3, lg: 4 }}
        onClick={navigateTo}
      >
        <NumberToPrice
          textProps={{
            fontSize: { lg: 'xl' },
            lineHeight: 1,
            fontWeight: 600,
            color: 'price',
          }}
          spanProps={{
            fontSize: { lg: 'xl' },
            lineHeight: 1,
            fontWeight: 600,
            color: 'price',
          }}
          value={product?.out_price + totalDiscount}
        />
        {totalDiscount !== 0 && (
          <Box
            textDecor={'line-through'}
            fontSize={{ base: 'xs', lg: 'sm' }}
            color='text.muted'
            mt={1}
          >
            <NumberToPrice
              textProps={{
                lineHeight: 1,
                fontWeight: 600,
              }}
              spanProps={{
                lineHeight: 1,
                fontWeight: 600,
              }}
              value={product?.out_price}
            />
          </Box>
        )}
        <Text
          fontSize={{ base: 'sm', lg: 'md' }}
          height={{ base: 'auto', lg: 10 }}
          lineHeight={1.2}
          mb={{ lg: 2 }}
          lineClamp={2}
          mt={2}
          color='text'
        >
          {resolveLocale(product?.title)}
        </Text>
        {product?.weight ? (
          <Text
            display={{ base: 'none', lg: 'block' }}
            color='text.muted'
            lineHeight='18px'
            opacity={product?.weight ? 1 : 0}
          >
            {product?.weight} {t('gram')}
          </Text>
        ) : null}
      </Card.Body>
      <Card.Footer
        px={{ base: 2, lg: 4 }}
        pb={{ base: 2, lg: 4 }}
        flexDir={'column'}
        justifyContent={'flex-start'}
        alignItems={'start'}
        color='product_button.text'
      >
        <AddToCart
          onClick={() => void handleAddClick()}
          quantity={quantity}
          isDisabled={!isActiveProduct}
          isOrdered={Boolean(productInCart)}
          onRemove={() => removeHandler()}
          setQuantity={(e: number) => {
            dispatch(SET_QUANTITY({ key: productInCart?.key, quantity: e }))
          }}
        >
          {isActiveProduct ? (
            <>
              {/* <NumberToPrice value={product?.out_price + totalDiscountPrice} /> */}
              <AddIcon color='currentColor' />{' '}
              <Text as='span' display={{ base: 'none', lg: 'inline' }}>
                {t('add_to_cart')}
              </Text>
            </>
          ) : (
            <Text as='span' fontWeight={500}>
              {t('will_be_later')}
            </Text>
          )}
        </AddToCart>
      </Card.Footer>
    </Card.Root>
  )
}

export default memo(ProductCard)
